import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useLanguage } from '../contexts/LanguageContext';
import { dashboardService } from '../api/services';
import { DashboardStats } from '../types';
import { formatTND } from '../utils/formatters';
import { StatusBadge } from '../components/common/StatusBadge';
import { MoneyDisplay } from '../components/common/MoneyDisplay';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { CarbonFootprintWidget } from '../components/dashboard/CarbonFootprintWidget';
import { GovernanceWidget } from '../components/dashboard/GovernanceWidget';
import {
  GFWallet,
  GFCalendar,
  GFAlert,
  GFUsers,
  GFArrowUp,
  GFArrowDown,
  GFPlus,
  GFDownload,
} from '../components/icons/GFIcons';
import toast from 'react-hot-toast';

export function Dashboard() {
  const { t } = useLanguage();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const data = await dashboardService.getStats();
      setStats(data);
    } catch (error) {
      toast.error(t.messages.loadError);
      setStats({
        caMensuel: 0,
        caAnnuel: 0,
        facturesImpayeesCount: 0,
        facturesImpayeesMontant: 0,
        nombreClients: 0,
        caMensuelParMois: [],
        topClients: [],
        dernieresFactures: [],
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }
  if (!stats) return null;

  const kpis = [
    {
      label: t.dashboard.caMensuel,
      value: formatTND(stats.caMensuel),
      icon: GFWallet,
    },
    {
      label: t.dashboard.caAnnuel,
      value: formatTND(stats.caAnnuel),
      icon: GFCalendar,
    },
    {
      label: t.dashboard.facturesImpayees,
      value: `${stats.facturesImpayeesCount}`,
      sub: formatTND(stats.facturesImpayeesMontant),
      icon: GFAlert,
      warn: stats.facturesImpayeesCount > 0,
    },
    {
      label: t.dashboard.nombreClients,
      value: `${stats.nombreClients}`,
      icon: GFUsers,
    },
  ];

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">{t.dashboard.title}</h1>
          <div className="page-subtitle">
            Aperçu en temps réel · {new Date().toLocaleDateString('fr-FR', { dateStyle: 'long' })}
          </div>
        </div>
        <div className="page-actions">
          <button type="button" className="gf-btn gf-btn-ghost gf-btn-sm">
            <GFDownload size={14} />
            Exporter
          </button>
          <button type="button" className="gf-btn gf-btn-primary gf-btn-sm">
            <GFPlus size={14} />
            Nouvelle facture
          </button>
        </div>
      </div>

      <div className="kpi-grid">
        {kpis.map((k, i) => {
          const Icon = k.icon;
          return (
            <div key={i} className="kpi">
              <div className="kpi-label">
                <Icon size={12} />
                <span>{k.label}</span>
              </div>
              <div className="kpi-value">{k.value}</div>
              {k.sub && <div className="kpi-sub">{k.sub}</div>}
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 24 }}>
        <GovernanceWidget />
      </div>

      <div style={{ marginTop: 24 }}>
        <CarbonFootprintWidget />
      </div>

      <div className="gf-card" style={{ marginTop: 24 }}>
        <div className="gf-card-header">
          <div>
            <div className="gf-card-title">{t.dashboard.caMensuel12Mois}</div>
            <div className="gf-card-subtitle">Chiffre d'affaires par mois (TND)</div>
          </div>
        </div>
        <div style={{ padding: 16, height: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={stats.caMensuelParMois}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--rule)" />
              <XAxis dataKey="mois" tick={{ fontSize: 12, fill: 'var(--ink-3)' }} stroke="var(--rule-strong)" />
              <YAxis
                tick={{ fontSize: 12, fill: 'var(--ink-3)' }}
                stroke="var(--rule-strong)"
                tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
              />
              <Tooltip
                formatter={(value: any) => [formatTND(Number(value)), t.dashboard.montant]}
                labelStyle={{ fontWeight: 600, color: 'var(--ink)' }}
                contentStyle={{
                  borderRadius: 10,
                  border: '1px solid var(--rule)',
                  background: 'var(--bg)',
                  boxShadow: 'var(--sh)',
                  fontSize: 12,
                }}
                cursor={{ fill: 'var(--bg-panel)' }}
              />
              <Bar dataKey="montant" fill="var(--brand-green)" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))',
          gap: 24,
          marginTop: 24,
        }}
      >
        <div className="gf-card">
          <div className="gf-card-header">
            <div>
              <div className="gf-card-title">{t.dashboard.topClients}</div>
              <div className="gf-card-subtitle">Par chiffre d'affaires</div>
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="min-w-full" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>{t.dashboard.client}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t.dashboard.montant}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t.dashboard.nombreFactures}</th>
                </tr>
              </thead>
              <tbody>
                {stats.topClients.length === 0 ? (
                  <tr>
                    <td colSpan={3} style={{ ...tdStyle, textAlign: 'center', color: 'var(--ink-3)' }}>
                      {t.common.noData}
                    </td>
                  </tr>
                ) : (
                  stats.topClients.map((client, idx) => (
                    <tr key={client.clientId} style={trStyle}>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span
                            style={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              background: 'var(--accent-soft)',
                              color: 'var(--brand-green-dark)',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 11,
                              fontWeight: 600,
                              fontFamily: 'var(--ff-mono)',
                            }}
                          >
                            {idx + 1}
                          </span>
                          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>
                            {client.raisonSociale}
                          </span>
                        </div>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <MoneyDisplay amount={client.totalCA} size="sm" />
                      </td>
                      <td
                        style={{
                          ...tdStyle,
                          textAlign: 'right',
                          fontFamily: 'var(--ff-mono)',
                          color: 'var(--ink-2)',
                        }}
                      >
                        {client.nombreFactures}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="gf-card">
          <div className="gf-card-header">
            <div>
              <div className="gf-card-title">{t.dashboard.dernieresFactures}</div>
              <div className="gf-card-subtitle">Activité récente</div>
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>N°</th>
                  <th style={thStyle}>{t.dashboard.client}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t.dashboard.montant}</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>{t.common.status}</th>
                </tr>
              </thead>
              <tbody>
                {stats.dernieresFactures.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ ...tdStyle, textAlign: 'center', color: 'var(--ink-3)' }}>
                      {t.common.noData}
                    </td>
                  </tr>
                ) : (
                  stats.dernieresFactures.map((facture) => (
                    <tr key={facture.id} style={trStyle}>
                      <td
                        style={{
                          ...tdStyle,
                          fontFamily: 'var(--ff-mono)',
                          fontWeight: 500,
                          color: 'var(--ink)',
                        }}
                      >
                        {facture.numero}
                      </td>
                      <td style={tdStyle}>{facture.clientRaisonSociale}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <MoneyDisplay amount={facture.netAPayer} size="sm" />
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <StatusBadge statut={facture.statutPaiement} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

const thStyle: React.CSSProperties = {
  fontFamily: 'var(--ff-mono)',
  fontSize: 10.5,
  fontWeight: 500,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'var(--ink-3)',
  textAlign: 'left',
  padding: '10px 16px',
  background: 'var(--bg-panel)',
  borderBottom: '1px solid var(--rule)',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 16px',
  fontSize: 13,
  color: 'var(--ink)',
  borderBottom: '1px solid var(--rule)',
  verticalAlign: 'middle',
  whiteSpace: 'nowrap',
};

const trStyle: React.CSSProperties = {};

export default Dashboard;
